/**
 * Single-open accordion, used by the FAQ topic pages and the Guide.
 *
 * Both implemented the same open-index pattern inline with byte-identical card
 * markup: a bordered surface-1 card, a 12px/14px header button, a 15px chevron
 * that rotates 180°, and body copy at 12px / 1.6 line height. That markup is
 * reproduced here exactly; only aria-expanded is added, which the originals
 * lacked — the expanded state was signalled by the chevron rotation alone.
 */
import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { c } from '../../styles/theme';

export interface AccordionItem {
  /** Stable key. Falls back to the index when absent. */
  id?: string;
  title: string;
  body: ReactNode;
}

export interface AccordionProps {
  items: AccordionItem[];
  /**
   * Index of the open item. -1 or null closes all — the prototype used -1, which
   * is why closing is not simply `null`.
   */
  openIndex: number | null;
  /** Called with the clicked index. The caller decides how to collapse. */
  onToggle: (index: number) => void;
  /** Gap between cards. 8 at both call sites. */
  gap?: number;
  marginBottom?: number;
}

export default function Accordion({
  items,
  openIndex,
  onToggle,
  gap = 8,
  marginBottom,
}: AccordionProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap, marginBottom }}>
      {items.map((item, i) => {
        const open = openIndex === i;
        const key = item.id ?? item.title;
        return (
          <div
            key={key}
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 10,
              background: c.surface1,
              overflow: 'hidden',
            }}
          >
            <button
              type="button"
              onClick={() => onToggle(i)}
              aria-expanded={open}
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 500, color: c.text1, textAlign: 'left' }}>
                {item.title}
              </span>
              <ChevronDown
                size={15}
                color={c.text2}
                style={{
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                  flexShrink: 0,
                  marginLeft: 10,
                }}
              />
            </button>
            {open && (
              <div style={{ padding: '0 14px 14px' }}>
                <p style={{ fontSize: 12, color: c.text2, margin: 0, lineHeight: 1.6 }}>
                  {item.body}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
